import React from "react";

import { Fabric, Theme, initializeIcons } from "@nuorder/components";
import { OpenRollupTypeDialog } from "./open-rollup-type";
import { IRollupSnapshotResponse, RollupSnapshotStatus, RollupType } from "containers/folders/types/server-rollups";
import { useFetchSnapshotStatus } from "containers/folders/hooks/server-rollups";
import { ROOT_ITEM, isRollupContainingTrashedItem, shouldRetryHookOnError } from "containers/folders/hooks/utils";
import { DRIVE_LINK, MAX_FAILURE_COUNT, SERVER_ROLLUP_REFECTCH_INTERNAL } from "containers/folders/utils/constants";
import { formatMessage } from "lib/globalize-util";
import { navigateTo } from "containers/folders/utils/navigator";

interface IOwnProps {
    snapshotId: string;
    collectionIds: string[];
    open: boolean;
    merchantId: string;
    router: any;
    createSnapshotError?: any;
    collectionNotFoundInRollup?: boolean;
}

initializeIcons();

export const ReopenServerRollupDialog: React.FC<IOwnProps> = ({
    open,
    snapshotId,
    collectionIds,
    merchantId,
    createSnapshotError,
    router,
    collectionNotFoundInRollup: collectionNotFoundInRollupProp,
}) => {
    const [rollupType, setRollupType] = React.useState<RollupType>(RollupType.Server);
    const [stopFetchingSnapshotStatus, setStopFetchingSnapshotStatus] = React.useState<boolean>(false);
    const [openInternally, setOpenInternally] = React.useState<boolean>(true);
    const [collectionNotFoundInRollup, setCollectionNotFoundInRollup] =
        React.useState<boolean>(collectionNotFoundInRollupProp);
    const { data: collectionSnapshot, error: readSnapshotStatusError } = useFetchSnapshotStatus(
        snapshotId,
        merchantId,
        {
            enabled: !!merchantId && !!snapshotId && rollupType === RollupType.Server,
            retry: (failureCount: number, err: any) => {
                if (!shouldRetryHookOnError(err)) {
                    if ([400, 404].includes(err.code)) {
                        setRollupType(null);
                    }
                    return false;
                }
                return failureCount <= MAX_FAILURE_COUNT;
            },
            onSettled: (data: IRollupSnapshotResponse, error: any) => {
                if (data?.status === RollupSnapshotStatus.Finished) {
                    if (isRollupContainingTrashedItem(data)) {
                        setRollupType(null);
                        setOpenInternally(true);
                        setCollectionNotFoundInRollup(true);
                    } else {
                        setOpenInternally(false);
                    }
                    setStopFetchingSnapshotStatus(true);
                } else if (error || data?.status !== RollupSnapshotStatus.Created) {
                    setRollupType(null);
                    setStopFetchingSnapshotStatus(true);
                }
            },
            refetchInterval: stopFetchingSnapshotStatus ? false : SERVER_ROLLUP_REFECTCH_INTERNAL,
            refetchIntervalInBackground: true,
            refetchOnWindowFocus: false,
        }
    );

    const handleClose = React.useCallback(() => {
        router.replace({
            pathname: DRIVE_LINK,
            query: {
                itemId: ROOT_ITEM,
                merchantId,
            },
        });
    }, [router, merchantId]);

    const handleOpen = React.useCallback(
        (assortmentIds: string[] | string, isQuickRollup?: boolean) => {
            const ids = Array.isArray(assortmentIds) ? assortmentIds : [assortmentIds];
            const pathname = `/assortment/${ids.join("|")}`;
            if (isQuickRollup) {
                navigateTo({
                    route: pathname,
                    newTab: true,
                    title: formatMessage("View Rollup"),
                    message: formatMessage("Do you want to open the Rollup?"),
                });
            } else {
                router.replace({
                    pathname,
                    query: {
                        ...router.location.query,
                    },
                });
            }
        },
        [router]
    );

    return (
        <Theme>
            <Fabric>
                <OpenRollupTypeDialog
                    onOpenRollup={handleOpen}
                    onClose={handleClose}
                    open={open && openInternally}
                    collectionIds={collectionIds}
                    collectionSnapshot={rollupType && collectionSnapshot}
                    serverRollupDisabled={
                        !!readSnapshotStatusError ||
                        collectionSnapshot?.status === RollupSnapshotStatus.Failed ||
                        !!createSnapshotError ||
                        (!rollupType &&
                            stopFetchingSnapshotStatus &&
                            collectionSnapshot?.status !== RollupSnapshotStatus.Finished)
                    }
                    isAuthorizationError={[401, 403].includes((createSnapshotError as any)?.code)}
                    collectionNotFoundInRollup={collectionNotFoundInRollup}
                />
            </Fabric>
        </Theme>
    );
};
